import { Tabs, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { cn } from "@/lib/utils";
import { ChatFilterMode } from "../types/chatMessageTypes";

interface ChatFilterTabsProps {
  selectedFilter: ChatFilterMode;
  onFilterChange: (filter: ChatFilterMode) => void;
  disabledFilters?: ChatFilterMode[];
}

const FILTER_LABELS: Record<ChatFilterMode, string> = {
  [ChatFilterMode.LIVE]: "Live",
  [ChatFilterMode.ASYNC]: "Async",
  [ChatFilterMode.COMBINED]: "Combined",
};

const TRACKER_MAP: Record<ChatFilterMode, string> = {
  [ChatFilterMode.LIVE]: TRACKERS.CHAT_FILTER_CHANGED_TO_LIVE,
  [ChatFilterMode.ASYNC]: TRACKERS.CHAT_FILTER_CHANGED_TO_ASYNC,
  [ChatFilterMode.COMBINED]: TRACKERS.CHAT_FILTER_CHANGED_TO_COMBINED,
};

const FILTERS = [
  ChatFilterMode.LIVE,
  ChatFilterMode.ASYNC,
  ChatFilterMode.COMBINED,
];

/**
 * Validates if the value is a valid ChatFilterMode
 */
const isValidFilterMode = (value: string): value is ChatFilterMode => {
  return FILTERS.includes(value as ChatFilterMode);
};

export const ChatFilterTabs = ({
  selectedFilter,
  onFilterChange,
  disabledFilters = [],
}: ChatFilterTabsProps) => {
  const handleValueChange = (value: string) => {
    // Validate the value before type assertion
    if (!isValidFilterMode(value)) {
      // Invalid filter mode - silently ignore to prevent UI errors
      return;
    }

    const filter = value as ChatFilterMode;
    trackEvent(TRACKER_MAP[filter]);
    onFilterChange(filter);
  };

  return (
    <Tabs value={selectedFilter} onValueChange={handleValueChange}>
      <TabsList aria-label="Chat message filter">
        {FILTERS.map((filter) => {
          const isDisabled = disabledFilters.includes(filter);

          return (
            <TabsTrigger
              key={filter}
              value={filter}
              disabled={isDisabled}
              className={cn(
                isDisabled &&
                  "text-disabled bg-secondary disabled:pointer-events-auto disabled:cursor-not-allowed",
              )}
            >
              {FILTER_LABELS[filter]}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
};
